<script>
  import MessagePreview from '@components/messenger/MessagePreview.svelte';
  import { Cloud, UserRound, UsersRound } from 'lucide-svelte';
  import { avatarContentUrl } from '$lib/avatar';

  export let conversation;
  export let selected = false;
  export let onToggle = () => {};

  let failedAvatarUrls = new Set();

  function getInitial(value = '') {
    return String(value || '?').trim().charAt(0)?.toUpperCase() || '?';
  }

  function avatarIsAvailable(url) {
    return Boolean(url) && !failedAvatarUrls.has(url);
  }

  function markAvatarUnavailable(url) {
    if (!url || failedAvatarUrls.has(url)) return;
    failedAvatarUrls = new Set([...failedAvatarUrls, url]);
  }

  function memberAvatarUrl(member) {
    return avatarContentUrl(member?.avatar);
  }

  $: title = conversation?.title || 'Cuộc trò chuyện';
  $: preview = conversation?.lastMessagePreview || (conversation?.type === 'group' ? 'Nhóm trò chuyện' : 'Bắt đầu cuộc trò chuyện');
  $: conversationKind = conversation?.type === 'group' ? 'Nhóm' : conversation?.type === 'cloud' ? 'Cloud' : 'Trò chuyện';
  $: isGroup = conversation?.type === 'group';
  $: avatarUrl = avatarContentUrl(conversation?.avatar || conversation?.avatarUrl);
  $: groupPreviewMembers = Array.isArray(conversation?.memberPreview) ? conversation.memberPreview.slice(0, 3) : [];
  $: memberCount = Math.max(groupPreviewMembers.length, Number(conversation?.memberCount) || 0);
  $: groupCountBadge = memberCount > 3 ? memberCount - 3 : 0;
</script>

<button class:selected class="target-row" type="button" onclick={() => onToggle(conversation?.conversationId)}>
  <div class:group-avatar={isGroup} class="avatar">
    {#if isGroup && !avatarIsAvailable(avatarUrl)}
      {#if groupPreviewMembers.length}
        <div class="group-grid">
          {#each groupPreviewMembers as member}
            {@const previewAvatarUrl = memberAvatarUrl(member)}
            <div class="group-member">
              {#if avatarIsAvailable(previewAvatarUrl)}
                <img
                  src={previewAvatarUrl}
                  alt={member?.name || 'Thành viên nhóm'}
                  loading="lazy"
                  decoding="async"
                  onerror={() => markAvatarUnavailable(previewAvatarUrl)} />
              {:else}
                <span>{getInitial(member?.name)}</span>
              {/if}
            </div>
          {/each}

          {#if groupCountBadge}
            <span class="group-count">+{groupCountBadge}</span>
          {/if}
        </div>
      {:else}
        <UsersRound size={21} strokeWidth={2.1} />
      {/if}
    {:else if avatarIsAvailable(avatarUrl)}
      <img
        src={avatarUrl}
        alt={title}
        loading="lazy"
        decoding="async"
        onerror={() => markAvatarUnavailable(avatarUrl)} />
    {:else if conversationKind === 'Cloud'}
      <div class="cloud-bg"></div>
      <span class="cloud-icon">
        <Cloud size={21} strokeWidth={2.2} />
      </span>
    {:else if conversationKind === 'Nhóm'}
      <UsersRound size={21} strokeWidth={2.1} />
    {:else}
      <div class="direct-fallback">
        <UserRound size={21} strokeWidth={2.1} />
        <span class="sr-only">{getInitial(title)}</span>
      </div>
    {/if}
  </div>

  <div class="content">
    <strong>{title}</strong>
    <MessagePreview {preview} className="target-preview" />
  </div>

  <div class="meta">
    <small>{conversation?.type === 'group' ? 'Nhóm' : conversation?.type === 'cloud' ? 'Cloud' : 'Riêng tư'}</small>
    <span class="checkmark"></span>
  </div>
</button>

<style>
  .target-row {
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr);
    align-items: center;
    gap: 0.78rem;
    width: 100%;
    min-height: 3.55rem;
    padding: 0.46rem 0.55rem;
    border: 1px solid transparent;
    border-radius: 0.18rem;
    text-align: left;
    background: transparent;
    transition:
      background 120ms ease;
  }

  .target-row:hover {
    background: rgba(248, 250, 252, 0.7);
  }

  .target-row.selected {
    border-color: transparent;
    background: transparent;
  }

  .target-row:focus {
    outline: none;
  }

  .target-row:focus-visible .checkmark {
    outline: 2px solid rgba(15, 111, 255, 0.42);
    outline-offset: 2px;
  }

  .avatar {
    position: relative;
    display: grid;
    place-items: center;
    width: 2.25rem;
    height: 2.25rem;
    overflow: hidden;
    border-radius: 0.65rem;
    border: 1px solid rgba(203, 213, 225, 0.9);
    color: #0284c7;
    background: #f0f9ff;
    font-size: 0.88rem;
    font-weight: 700;
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .group-avatar {
    border-radius: 0.8rem;
    color: #7c3aed;
    background: #f5f3ff;
  }

  .group-grid {
    position: relative;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1px;
    width: 100%;
    height: 100%;
    background: #ffffff;
  }

  .group-member {
    display: grid;
    place-items: center;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    color: #64748b;
    background: #f1f5f9;
    font-size: 0.58rem;
    font-weight: 700;
  }

  .group-count {
    position: absolute;
    right: -0.1rem;
    bottom: -0.1rem;
    display: grid;
    place-items: center;
    min-width: 1.15rem;
    height: 1.15rem;
    padding: 0 0.2rem;
    border: 2px solid white;
    border-radius: 999px;
    color: #334155;
    background: #e2e8f0;
    font-size: 0.56rem;
    font-weight: 800;
    line-height: 1;
  }

  .cloud-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #60a5fa, #4f46e5);
  }

  .cloud-icon {
    position: relative;
    color: white;
  }

  .direct-fallback {
    display: grid;
    place-items: center;
    width: 100%;
    height: 100%;
    background: #e0f2fe;
    color: #0284c7;
  }

  .content {
    min-width: 0;
    align-self: center;
  }

  .content strong,
  .meta small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .content strong {
    color: #0f172a;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.25;
  }

  :global(.target-preview) {
    margin-top: 0.32rem;
    padding: 0;
  }

  .meta {
    display: grid;
    order: -1;
    justify-items: center;
  }

  .meta small {
    display: none;
  }

  .checkmark {
    display: grid;
    place-items: center;
    width: 1rem;
    height: 1rem;
    border: 1px solid rgba(148, 163, 184, 0.48);
    border-radius: 999px;
    background: white;
    color: white;
    font-size: 0.68rem;
    font-weight: 800;
    transition:
      background 120ms ease,
      border-color 120ms ease,
      transform 120ms ease;
  }

  .checkmark::after {
    content: '✓';
    opacity: 0;
  }

  .target-row.selected .checkmark {
    border-color: #0f9ff5;
    background: #0f9ff5;
    transform: scale(1.06);
  }

  .target-row.selected .checkmark::after {
    opacity: 1;
  }

  @media (max-width: 640px) {
    .target-row {
      grid-template-columns: auto auto minmax(0, 1fr);
      gap: 1.05rem;
      min-height: 5.35rem;
      padding: 0.65rem 0.1rem;
      border-radius: 0;
      background: transparent;
    }

    .target-row:hover,
    .target-row.selected {
      transform: none;
      border-color: transparent;
      background: transparent;
    }

    .avatar {
      width: 3.45rem;
      height: 3.45rem;
      border-radius: 999px;
      font-size: 1.1rem;
    }

    .content strong {
      font-size: 1.34rem;
      font-weight: 500;
    }

    :global(.target-preview) {
      display: none;
    }

    .checkmark {
      width: 1.85rem;
      height: 1.85rem;
      border-color: #cfd8e3;
      font-size: 1.15rem;
    }
  }
</style>
